import { GitBranch, GitCommitHorizontal, RefreshCw } from "lucide-react"
import { observer } from "mobx-react"
import { type ReactNode, useEffect, useMemo, useState } from "react"
import { twMerge } from "tailwind-merge"
import { type BranchInfo, type ChangedFileInfo, type GitLogEntry, gitApi } from "../nestAPIs/git"
import { useCodeStore } from "../store/context"
import type { ChangesManager } from "../store/managers/ChangesManager"
import { getPatchContextLines } from "../utils/gitDiffContext"
import { isTestFilePath } from "../utils/gitTestFileFilter"
import { CompactGitReview, type GitPatchSource, SelectedFileGitReview } from "./CompactGitReview"
import { ChangedFilesTree } from "./git/ChangedFilesTree"
import { DiffContextSelect, DiffStrategySelect, type ViewMode, ViewModeToggle } from "./git/shared"
import { TestFileFilter } from "./git/TestFileFilter"
import { Select } from "./ui/Select"
import { buildFileTree, collectAllDirPaths, collectFileTreeFiles, flattenFileTree } from "./utils/changesTree"

const PAGE_SIZE = 50
type DiffSource = "uncommitted" | "from-base"

const DIFF_SOURCE_ENTRIES = [
    { id: "uncommitted" as DiffSource, content: "Uncommitted" },
    { id: "from-base" as DiffSource, content: "From base" },
]

function DiffSourceSelect({ value, onChange }: { value: DiffSource; onChange: (value: DiffSource) => void }) {
    return (
        <Select
            selectedId={value}
            entries={DIFF_SOURCE_ENTRIES}
            onSelect={(entry) => onChange(entry.id)}
            className={{
                trigger: "h-8 px-2 text-xs border border-border bg-base-100 hover:bg-base-200 transition-colors",
                value: "text-xs",
            }}
        />
    )
}

interface GitLogTrayProps {
    changesManager: ChangesManager
    taskId: string
    workDir: string
    workspaceId: string
    workspaceRoot: string
    worktreeId?: string
    currentBranch: string | null
    taskStartCommit?: string
    className?: string
    compact?: boolean
}

interface ScopeOption {
    id: string
    content: ReactNode
    workDir: string
    workspaceRoot: string
    worktreeId?: string
    ref?: string
}

function buildScopeOptions({
    workDir,
    workspaceRoot,
    worktreeId,
    branches,
}: {
    workDir: string
    workspaceRoot: string
    worktreeId?: string
    branches: { name: string }[]
}): ScopeOption[] {
    const branchNames = new Set<string>()
    const branchOptions: ScopeOption[] = []

    const addBranch = (name: string) => {
        if (branchNames.has(name)) return
        branchNames.add(name)
        branchOptions.push({
            id: `branch:${name}`,
            workDir,
            workspaceRoot,
            ...(worktreeId ? { worktreeId } : {}),
            ref: name,
            content: (
                <div className="flex items-center gap-2 min-w-0">
                    <span className="text-[10px] uppercase tracking-wide text-muted flex-shrink-0">Branch</span>
                    <span className="truncate">{name}</span>
                </div>
            ),
        })
    }

    addBranch("HEAD")
    for (const primaryBranch of ["main", "master"]) {
        if (branches.some((branch) => branch.name === primaryBranch)) {
            addBranch(primaryBranch)
        }
    }
    for (const branch of branches) {
        addBranch(branch.name)
    }
    return branchOptions
}

function sameCommit(left: string, right: string): boolean {
    if (left === right) return true
    if (left.length < 7 || right.length < 7) return false
    return left.startsWith(right) || right.startsWith(left)
}

function CommitMarkers({ branches, taskStart }: { branches: BranchInfo[]; taskStart: boolean }) {
    if (!taskStart && branches.length === 0) return null
    const visibleBranches = branches.slice(0, 2)
    const hiddenBranches = branches.slice(visibleBranches.length)
    return (
        <div
            className="ml-auto flex min-w-0 items-center justify-end gap-1 overflow-hidden"
            title={branches.map((branch) => branch.name).join(", ") || undefined}
        >
            {taskStart && (
                <span className="shrink-0 border border-primary/30 bg-primary/10 px-1 py-px text-[9px] font-medium text-primary" data-task-start-marker>
                    Task start
                </span>
            )}
            {visibleBranches.map((branch) => (
                <span
                    key={branch.name}
                    className="flex max-w-24 min-w-0 items-center gap-1 border border-border bg-base-100 px-1 py-px text-[9px] text-base-content"
                    data-git-ref={branch.name}
                >
                    <GitBranch size={9} className="shrink-0 text-muted" />
                    <span className="truncate">{branch.name}</span>
                </span>
            ))}
            {hiddenBranches.length > 0 && (
                <span
                    className="shrink-0 border border-border bg-base-100 px-1 py-px text-[9px] text-muted"
                    title={hiddenBranches.map((branch) => branch.name).join(", ")}
                >
                    +{hiddenBranches.length}
                </span>
            )}
        </div>
    )
}

const DesktopGitLogTray = observer(function DesktopGitLogTray({
    changesManager,
    taskId,
    workDir,
    workspaceId,
    workspaceRoot,
    worktreeId,
    currentBranch,
    taskStartCommit,
    className,
}: GitLogTrayProps) {
    const codeStore = useCodeStore()

    const [scopeOptions, setScopeOptions] = useState<ScopeOption[]>([])
    const [branches, setBranches] = useState<BranchInfo[]>([])
    const [scopeLoading, setScopeLoading] = useState(false)
    const [scopeRefreshToken, setScopeRefreshToken] = useState(0)
    const [selectedScopeId, setSelectedScopeId] = useState<string>(() => (currentBranch ? `branch:${currentBranch}` : "branch:HEAD"))

    const [commits, setCommits] = useState<GitLogEntry[]>([])
    const [logLoading, setLogLoading] = useState(false)
    const [loadingMore, setLoadingMore] = useState(false)
    const [hasMore, setHasMore] = useState(false)
    const [selectedCommitSha, setSelectedCommitSha] = useState<string | null>(null)
    const [logError, setLogError] = useState<string | null>(null)

    const [commitFiles, setCommitFiles] = useState<ChangedFileInfo[]>([])
    const [commitFilesLoading, setCommitFilesLoading] = useState(false)
    const [selectedFilePath, setSelectedFilePath] = useState<string | null>(null)
    const [fileError, setFileError] = useState<string | null>(null)
    const diffContext = codeStore.ui.diffContext
    const patchContextLines = getPatchContextLines(diffContext)
    const viewMode = codeStore.ui.viewMode
    const diffStyle = viewMode === "split" ? "split" : "unified"
    const diffStrategy = codeStore.gitDiffStrategy
    const showTestFiles = codeStore.showTestFiles
    const setDiffContext = (context: typeof diffContext) => codeStore.ui.setDiffContext(context)
    const setViewMode = (mode: ViewMode) => codeStore.ui.setViewMode(mode)
    const setDiffStrategy = (strategy: typeof diffStrategy) => {
        codeStore.setGitDiffStrategy(strategy)
        if (strategy !== "current" && viewMode === "current") {
            setViewMode("unified")
        }
    }

    const selectedScope = useMemo(() => scopeOptions.find((option) => option.id === selectedScopeId) ?? null, [scopeOptions, selectedScopeId])

    const reviewingUncommitted = selectedCommitSha === null
    const selectedCommit = useMemo(() => commits.find((commit) => commit.sha === selectedCommitSha) ?? null, [commits, selectedCommitSha])
    const branchesByCommit = useMemo(() => {
        const result = new Map<string, BranchInfo[]>()
        for (const branch of branches) {
            if (!branch.sha || branch.name.endsWith("/HEAD")) continue
            const entries = result.get(branch.sha) ?? []
            entries.push(branch)
            result.set(branch.sha, entries)
        }
        return result
    }, [branches])

    const commitTestFileCount = useMemo(() => commitFiles.filter((file) => isTestFilePath(file.path)).length, [commitFiles])
    const visibleCommitFiles = useMemo(
        () => (showTestFiles ? commitFiles : commitFiles.filter((file) => !isTestFilePath(file.path))),
        [commitFiles, showTestFiles]
    )
    const commitFileTree = useMemo(() => buildFileTree(visibleCommitFiles), [visibleCommitFiles])
    const orderedCommitFiles = useMemo(() => collectFileTreeFiles(commitFileTree), [commitFileTree])
    const selectedCommitFile = useMemo(() => {
        if (orderedCommitFiles.length === 0) return null
        if (!selectedFilePath) return orderedCommitFiles[0]
        return orderedCommitFiles.find((file) => file.path === selectedFilePath) ?? orderedCommitFiles[0]
    }, [orderedCommitFiles, selectedFilePath])
    const commitFileEntries = useMemo(() => flattenFileTree(commitFileTree, collectAllDirPaths(commitFileTree)), [commitFileTree])
    const uncommittedFileTree = useMemo(() => buildFileTree(changesManager.files), [changesManager.files])
    const orderedUncommittedFiles = useMemo(() => collectFileTreeFiles(uncommittedFileTree), [uncommittedFileTree])
    const selectedUncommittedFile = useMemo(() => {
        if (orderedUncommittedFiles.length === 0) return null
        const selectedPath = changesManager.selectedFilePath
        return orderedUncommittedFiles.find((file) => file.path === selectedPath) ?? orderedUncommittedFiles[0]
    }, [changesManager.selectedFilePath, orderedUncommittedFiles])
    const reviewFiles = reviewingUncommitted ? orderedUncommittedFiles : orderedCommitFiles
    const reviewFile = reviewingUncommitted ? selectedUncommittedFile : selectedCommitFile
    const reviewFileEntries = reviewingUncommitted ? changesManager.flatEntries : commitFileEntries
    const reviewFilesLoading = reviewingUncommitted ? changesManager.isLoading : commitFilesLoading
    const testFileCount = reviewingUncommitted ? changesManager.testFileCount : commitTestFileCount
    const testFileSettings = reviewingUncommitted ? changesManager : codeStore
    const reviewLabel = reviewingUncommitted ? (changesManager.diffSource === "from-base" ? "From base" : "Uncommitted") : (selectedCommit?.message ?? "Commit")
    const patchSource = useMemo<GitPatchSource>(
        () =>
            reviewingUncommitted
                ? { kind: "worktree", fromTreeish: changesManager.fromTreeish }
                : { kind: "commit", commit: selectedCommit?.sha ?? "", parentCount: selectedCommit?.parentCount ?? 0 },
        [changesManager.fromTreeish, reviewingUncommitted, selectedCommit?.parentCount, selectedCommit?.sha]
    )
    const allReviewFilesFiltered = !showTestFiles && testFileCount > 0 && reviewFiles.length === 0
    const emptyReviewFilesMessage = allReviewFilesFiltered
        ? `All ${testFileCount} changed test ${testFileCount === 1 ? "file is" : "files are"} excluded.`
        : reviewingUncommitted
          ? "No changes."
          : "No file changes in this commit."

    useEffect(() => {
        let cancelled = false
        setScopeLoading(true)

        async function loadScopes() {
            try {
                const branchesResult = await gitApi.listBranches({
                    workspaceId,
                    workspaceRoot,
                    directory: workDir,
                    ...(worktreeId ? { worktreeId } : {}),
                    includeRemote: true,
                })
                if (cancelled) return

                const options = buildScopeOptions({
                    workDir,
                    workspaceRoot,
                    worktreeId,
                    branches: branchesResult.branches,
                })
                setBranches(branchesResult.branches)
                setScopeOptions(options)
                setSelectedScopeId((previousId) => {
                    if (options.some((option) => option.id === previousId)) {
                        return previousId
                    }

                    const preferredScope = currentBranch ? `branch:${currentBranch}` : "branch:HEAD"
                    if (options.some((option) => option.id === preferredScope)) {
                        return preferredScope
                    }

                    return options[0]?.id ?? "branch:HEAD"
                })
            } catch (error) {
                console.error("[GitLogTray] Failed to load branch scopes:", error)
                if (cancelled) return
                const fallback = buildScopeOptions({
                    workDir,
                    workspaceRoot,
                    worktreeId,
                    branches: [],
                })
                setBranches([])
                setScopeOptions(fallback)
                const preferredScope = currentBranch ? `branch:${currentBranch}` : "branch:HEAD"
                setSelectedScopeId(fallback.some((option) => option.id === preferredScope) ? preferredScope : (fallback[0]?.id ?? "branch:HEAD"))
            } finally {
                if (!cancelled) {
                    setScopeLoading(false)
                }
            }
        }

        loadScopes()
        return () => {
            cancelled = true
        }
    }, [workspaceId, workDir, workspaceRoot, worktreeId, currentBranch, scopeRefreshToken])

    useEffect(() => {
        const scope = selectedScope
        if (!scope) {
            setCommits([])
            setHasMore(false)
            setSelectedCommitSha(null)
            return
        }

        let cancelled = false
        setLogLoading(true)
        setLogError(null)
        setCommits([])
        setHasMore(false)
        setSelectedCommitSha(null)

        async function loadLog(scopeValue: ScopeOption) {
            try {
                const result = await gitApi.getLog({
                    workspaceId,
                    workspaceRoot: scopeValue.workspaceRoot,
                    directory: scopeValue.workDir,
                    worktreeId: scopeValue.worktreeId,
                    ref: scopeValue.ref,
                    limit: PAGE_SIZE,
                    skip: 0,
                })
                if (cancelled) return
                setCommits(result.commits)
                setHasMore(result.hasMore)
            } catch (error) {
                console.error("[GitLogTray] Failed to load commit log:", error)
                if (cancelled) return
                setCommits([])
                setHasMore(false)
                setSelectedCommitSha(null)
                setLogError("Failed to load commit history")
            } finally {
                if (!cancelled) {
                    setLogLoading(false)
                }
            }
        }

        loadLog(scope)
        return () => {
            cancelled = true
        }
    }, [selectedScope])

    useEffect(() => {
        const scope = selectedScope
        const commit = selectedCommit
        if (!scope || !commit) {
            setCommitFiles([])
            setSelectedFilePath(null)
            return
        }

        let cancelled = false
        setCommitFilesLoading(true)
        setFileError(null)

        async function loadCommitFiles(scopeValue: ScopeOption, commitValue: GitLogEntry) {
            try {
                const result = await gitApi.getCommitFiles({
                    workspaceId,
                    workspaceRoot: scopeValue.workspaceRoot,
                    directory: scopeValue.workDir,
                    worktreeId: scopeValue.worktreeId,
                    commit: commitValue.sha,
                })
                if (cancelled) return
                setCommitFiles(result.files)
            } catch (error) {
                console.error("[GitLogTray] Failed to load commit files:", error)
                if (cancelled) return
                setCommitFiles([])
                setFileError("Failed to load changed files for commit")
            } finally {
                if (!cancelled) {
                    setCommitFilesLoading(false)
                }
            }
        }

        loadCommitFiles(scope, commit)
        return () => {
            cancelled = true
        }
    }, [selectedScope, selectedCommit])

    useEffect(() => {
        setSelectedFilePath((previousPath) => {
            if (orderedCommitFiles.length === 0) return null
            if (previousPath && orderedCommitFiles.some((file) => file.path === previousPath)) {
                return previousPath
            }
            return orderedCommitFiles[0].path
        })
    }, [orderedCommitFiles])

    const refresh = () => {
        changesManager.refresh()
        setScopeRefreshToken((value) => value + 1)
    }

    const loadMore = async () => {
        if (!selectedScope || loadingMore || logLoading || !hasMore) {
            return
        }

        setLoadingMore(true)
        try {
            const result = await gitApi.getLog({
                workspaceId,
                workspaceRoot: selectedScope.workspaceRoot,
                directory: selectedScope.workDir,
                worktreeId: selectedScope.worktreeId,
                ref: selectedScope.ref,
                limit: PAGE_SIZE,
                skip: commits.length,
            })
            setCommits((previous) => [...previous, ...result.commits])
            setHasMore(result.hasMore)
        } catch (error) {
            console.error("[GitLogTray] Failed to load more commits:", error)
            setLogError("Failed to load more commits")
        } finally {
            setLoadingMore(false)
        }
    }

    return (
        <div className={twMerge("flex flex-col h-full", className)}>
            <div className="px-3 py-2 border-b border-border flex items-center gap-3 bg-base-200 min-w-0">
                <Select
                    selectedId={selectedScopeId}
                    entries={scopeOptions}
                    onSelect={(entry) => setSelectedScopeId(entry.id)}
                    disabled={scopeLoading || scopeOptions.length === 0}
                    className={{
                        trigger: "h-8 px-2 text-xs border border-border bg-base-100 hover:bg-base-200 transition-colors min-w-[16rem]",
                        value: "text-xs truncate",
                    }}
                />
                {reviewingUncommitted && changesManager.canCompareFromBase ? (
                    <DiffSourceSelect value={changesManager.diffSource} onChange={(value) => changesManager.setDiffSource(value)} />
                ) : null}
                <TestFileFilter settings={testFileSettings} testFileCount={testFileCount} />
                <div
                    className="flex-1 min-w-0 flex items-center gap-2 text-sm"
                    title={selectedCommit ? `${selectedCommit.shortSha} ${selectedCommit.message}` : reviewLabel}
                >
                    {selectedCommit ? (
                        <>
                            <span className="font-mono text-xs text-muted flex-shrink-0">{selectedCommit.shortSha}</span>
                            <span className="truncate text-base-content">{selectedCommit.message}</span>
                            <span className="hidden lg:block text-xs text-muted truncate shrink min-w-0 max-w-44">
                                {selectedCommit.author} · {selectedCommit.relativeDate}
                            </span>
                        </>
                    ) : (
                        <>
                            <span className="truncate text-base-content">{reviewLabel}</span>
                            <span className="hidden text-xs text-muted lg:block">
                                {reviewFiles.length} changed file{reviewFiles.length === 1 ? "" : "s"}
                            </span>
                        </>
                    )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                        type="button"
                        onClick={refresh}
                        className="btn p-1 text-muted hover:text-base-content hover:bg-base-300 transition-colors"
                        title="Refresh history"
                        disabled={scopeLoading || logLoading}
                    >
                        <RefreshCw size={14} />
                    </button>
                    <DiffContextSelect value={diffContext} onChange={setDiffContext} />
                    <DiffStrategySelect value={diffStrategy} onChange={setDiffStrategy} />
                    <ViewModeToggle value={viewMode} onChange={setViewMode} allowCurrent={false} />
                </div>
            </div>
            <div className="flex-1 min-h-0 flex">
                <div className="flex w-64 flex-col border-r border-border bg-base-200">
                    <div className="flex-1 overflow-y-auto">
                        <div className="flex flex-col py-1">
                            <button
                                type="button"
                                onClick={() => setSelectedCommitSha(null)}
                                className={twMerge(
                                    "btn border-b border-border/40 px-3 py-1.5 text-left transition-colors",
                                    reviewingUncommitted ? "bg-primary/10 text-base-content" : "text-muted hover:bg-base-300 hover:text-base-content"
                                )}
                                title="Uncommitted working-tree changes"
                                data-history-entry="uncommitted"
                            >
                                <div className="text-xs font-medium">Uncommitted</div>
                                <div className="mt-0.5 truncate text-[10px] text-muted">
                                    {changesManager.isLoading
                                        ? "Loading working tree…"
                                        : `${changesManager.files.length} changed file${changesManager.files.length === 1 ? "" : "s"}`}
                                </div>
                            </button>
                            {logLoading && commits.length === 0 ? (
                                <div className="py-8 text-center text-sm text-muted">Loading commits...</div>
                            ) : commits.length === 0 ? (
                                <div className="py-8 text-center text-sm text-muted">No commits found.</div>
                            ) : (
                                commits.map((commit) => {
                                    const selected = commit.sha === selectedCommit?.sha
                                    return (
                                        <button
                                            key={commit.sha}
                                            type="button"
                                            onClick={() => setSelectedCommitSha(commit.sha)}
                                            className={twMerge(
                                                "btn border-b border-border/40 px-3 py-1.5 text-left transition-colors",
                                                selected ? "bg-primary/10 text-base-content" : "text-muted hover:text-base-content hover:bg-base-300"
                                            )}
                                            title={`${commit.shortSha} ${commit.message}`}
                                        >
                                            <div className="flex min-w-0 items-center gap-1.5 font-mono text-[11px] text-muted">
                                                <GitCommitHorizontal size={12} className="shrink-0" />
                                                <span className="shrink-0">{commit.shortSha}</span>
                                                <CommitMarkers
                                                    branches={branchesByCommit.get(commit.sha) ?? []}
                                                    taskStart={taskStartCommit ? sameCommit(commit.sha, taskStartCommit) : false}
                                                />
                                            </div>
                                            <div className="mt-0.5 truncate text-xs text-base-content">{commit.message}</div>
                                            <div className="mt-0.5 truncate text-[10px] text-muted">
                                                {commit.author} · {commit.relativeDate}
                                            </div>
                                        </button>
                                    )
                                })
                            )}
                        </div>
                    </div>
                    {hasMore && (
                        <div className="p-2 border-t border-border">
                            <button
                                type="button"
                                onClick={loadMore}
                                disabled={loadingMore}
                                className="btn w-full h-8 flex items-center justify-center text-xs border border-border bg-base-100 hover:bg-base-300 transition-colors disabled:opacity-60"
                            >
                                {loadingMore ? "Loading..." : "Load more"}
                            </button>
                        </div>
                    )}
                </div>
                <div className="flex-1 min-w-0 flex flex-col">
                    {reviewingUncommitted || selectedCommit ? (
                        <>
                            {reviewFilesLoading ? (
                                <div className="flex-1 flex items-center justify-center py-12 text-muted text-sm">Loading changed files...</div>
                            ) : reviewFiles.length === 0 ? (
                                <div className="flex-1 flex items-center justify-center py-12 text-muted text-sm">{emptyReviewFilesMessage}</div>
                            ) : (
                                <div className="flex min-h-0 flex-1">
                                    <div className="flex w-64 flex-shrink-0 flex-col border-r border-border bg-base-200">
                                        <div className="border-b border-border px-3 py-2 text-xs text-muted">
                                            {reviewFiles.length} changed file{reviewFiles.length === 1 ? "" : "s"}
                                        </div>
                                        <ChangedFilesTree
                                            entries={reviewFileEntries}
                                            selectedFilePath={reviewFile?.path ?? null}
                                            workingDir={workDir}
                                            className="min-h-0 flex-1"
                                            onSelect={(path) => {
                                                if (reviewingUncommitted) {
                                                    changesManager.selectFile(path)
                                                } else {
                                                    setSelectedFilePath(path)
                                                }
                                            }}
                                        />
                                    </div>
                                    {reviewFile && (
                                        <SelectedFileGitReview
                                            file={reviewFile}
                                            patchSource={patchSource}
                                            contextLines={patchContextLines}
                                            diffStyle={diffStyle}
                                            diffStrategy={diffStrategy}
                                            showPathActions
                                            className="min-w-0 flex-1"
                                            taskId={taskId}
                                            workDir={workDir}
                                            workspaceId={workspaceId}
                                            workspaceRoot={workspaceRoot}
                                            worktreeId={worktreeId}
                                        />
                                    )}
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="flex-1 flex items-center justify-center py-12 text-muted text-sm">Select a commit to view changes</div>
                    )}
                    {(logError || fileError) && <div className="px-3 py-2 border-t border-border text-xs text-error bg-error/5">{logError ?? fileError}</div>}
                </div>
            </div>
        </div>
    )
})

export const GitLogTray = observer(function GitLogTray(props: GitLogTrayProps): JSX.Element {
    if (props.compact) {
        return (
            <CompactGitReview
                changesManager={props.changesManager}
                taskId={props.taskId}
                workDir={props.workDir}
                workspaceId={props.workspaceId}
                workspaceRoot={props.workspaceRoot}
                worktreeId={props.worktreeId}
                className={props.className}
            />
        )
    }
    return <DesktopGitLogTray {...props} />
})
